import styled from "styled-components/macro";
import {Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Input} from "@chakra-ui/react";
import {useEffect, useState} from "react";
import {useActions} from "../../store";
import {useFeedback} from "../../store/feedback";
import {useUpdateFeedList} from "../../api/custom_feeds";
import capitalization from "../../utils/capitalization";
import {themecolors} from "../../utils/themecolors";
import {Tag, List as LucideList, Keyboard, User, Link, Delete as LucideDelete} from "lucide-react";

const Heading = styled.div``;
const FeedCategory = styled.div``;
const FeedHeading = styled.div``;
const SecondaryList = styled.div``;
const Item = styled.div``;
const Delete = styled.div``;

const List = styled(({list, include, type, save, className}) => {
    const name = `${include ? 'exclude' : 'include'  }d${  capitalization(type)}`;
    const [value, setValue] = useState('');
    const [items, setItems] = useState(list);
    useEffect(() => {
        setItems(list);
    }, [list])
    const handleDelete = (value) => {
        setItems(
            items.filter((item) => {
                return item !== value;
            })
        );
        save(name, items.filter((item) => {return item !== value;}));
    };
    const handleAdd = (e) => {
        if (e.key === 'Enter') {
            setItems([...items, value]);
            setValue('');
            // save changes to API
            save(name, [... items, value]);
        }
    };
    return (
        <div className={className}>
            {items?.map((item, i) =>
                <Item key={i}>
                    {item}
                    <Delete onClick={() => handleDelete(item)}>
                        <LucideDelete size={16} />
                    </Delete>
                </Item>
            )}
            <Input placeholder={`Enter ${type}...`}
                   value={value}
                   onChange={(e) => setValue(e.currentTarget.value)}
                   onKeyPress={(e) => handleAdd(e)}
                   />
        </div>
    );
})`
  ${Item} {
    display: flex;
    padding: 4px 0;
    font-family: monospace;
  }
  ${Delete} {
    margin-left: auto;
    cursor: pointer;
  }
`;

const ConfigList = styled(({title, include, list, secondaryList, type, save, className}) => {
    return (
        <AccordionItem className={className}>
            <h2>
                <AccordionButton>
                    <FeedHeading>
                        {title}
                    </FeedHeading>
                    <AccordionIcon />
                </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
                <p>{include ? 'Exclude' : 'Include'} these {type}:</p>
                <List list={list} include={include} type={type} save={save} />
                {secondaryList && <SecondaryList>
                    <p>Always {include ? 'include' : 'exclude'} these {type}:</p>
                    <List list={secondaryList} include={!include} type={type} save={save} />
                </SecondaryList>}
            </AccordionPanel>
        </AccordionItem>
    );
})`
  p {
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 6px;
  }

  ${SecondaryList} {
    margin-top: 12px;
  }
`;

const FeedConfig = styled(({feed, className}) => {

    const actions = useActions();

    const {error, feedbackCh} = useFeedback();
    const saveSettings = useUpdateFeedList(feedbackCh);

    const handleSave = async (name, list) => {
        try {
            const obj = {id: feed.id};
            obj[name] = JSON.stringify(list);
            await saveSettings(obj);
        } catch {
            // error is displayed
        }
    };

    return (
        <div className={className}>

            <Heading>Feed Settings</Heading>

            <Accordion allowToggle>

                {feed.include_all ?
                    <>
                        <ConfigList save={handleSave} include={feed.include_all} title={<><LucideList size={20} /> Communities</>} type='communities' list={feed.excluded_communities} secondaryList={feed.included_communities}/>
                        <ConfigList save={handleSave} include={feed.include_all} title={<><Tag size={20} /> Topics</>} type='topics' list={feed.excluded_topics}/>
                        <ConfigList save={handleSave} include={feed.include_all} title={<><Keyboard size={20} /> Keywords</>} type='keywords' list={feed.excluded_keywords}/>
                        <ConfigList save={handleSave} include={feed.include_all} title={<><User size={20} /> Users</>} type='users' list={feed.excluded_users}/>
                        <ConfigList save={handleSave} include={feed.include_all} title={<><Link size={20} /> Domains</>} type='domains' list={feed.excluded_domains}/>
                    </>
                    :
                    <>
                        <ConfigList save={handleSave} include={feed.include_all} title={<><LucideList size={20} /> Communities</>} type='communities' list={feed.included_communities} secondaryList={feed.excluded_communities} />
                        <ConfigList save={handleSave} include={feed.include_all} title={<><Tag size={20} /> Topics</>} type='topics' list={feed.included_topics}/>
                        <ConfigList save={handleSave} include={feed.include_all} title={<><Keyboard size={20} /> Keywords</>} type='keywords' list={feed.included_keywords}/>
                        <ConfigList save={handleSave} include={feed.include_all} title={<><User size={20} /> Users</>} type='users' list={feed.included_users}/>
                        <ConfigList save={handleSave} include={feed.include_all} title={<><Link size={20} /> Domains</>} type='domains' list={feed.included_domains}/>
                    </>
                }

            </Accordion>

        </div>
    )
})`
  background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_2 : themecolors.pure_white};
  padding: 16px 8px;
  margin-bottom: 16px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  
  ${Heading} {
    margin-bottom: 12px;
    padding: 0 8px;
    font-weight: 500;
  }
  ${FeedCategory} {
    padding: 0 8px;
  }
  ${FeedHeading} {
    display: flex;
    align-items: center;
    font-size: 14px;
    padding: 4px 0;
    flex-grow: 1;
  }
  ${FeedHeading} svg {
    margin-right: 8px;
  }
`;

export default FeedConfig;
